import { Link } from 'react-router-dom'
import { BrandLockup } from '@/components/BrandLogo'

/**
 * The suite footer, carried by every marketing page.
 *
 * <p>Same construction as every other NevTan product: the deep teal ground,
 * a brand column holding the lockup, a sentence and the product's own trust
 * chips, a four-column link grid beside it, and a hairline bottom bar carrying
 * the copyright and the company's legal links.
 *
 * <p>Every destination here is one that actually exists. The company links go
 * to nevtan.com because that is where NevTan publishes them for the whole
 * suite, rather than to routes this application does not serve.
 */
export function SiteFooter() {
  return (
    <footer className="bg-brand-footer">
      <div className="nv-container py-14 sm:py-16">
        <div className="flex flex-col gap-12 lg:flex-row lg:gap-14">
          <div className="lg:max-w-[320px]">
            {/* The footer sits on the deep teal, so the white lockup is the
                readable variant. */}
            <BrandLockup tone="white" markClassName="h-9 w-9" textClassName="text-[22px]" />
            <p className="mt-4 text-[14px] leading-relaxed text-white/70">
              One place to find, install, and run open AI models on hardware you already own.
              Private by design, with nothing charged per message, and built to work with
              the rest of the NevTan suite.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {['🔒 Runs on your machine', '🚫 No per-message cost', '✅ Open models'].map(
                (chip) => (
                  <span
                    key={chip}
                    className="inline-flex items-center rounded-lg bg-white/10 px-2 py-1 text-[11.5px] font-semibold text-white/90"
                  >
                    {chip}
                  </span>
                ),
              )}
            </div>
          </div>

          <div className="grid flex-1 grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-4">
            <FooterColumn
              title="Product"
              links={[
                { label: 'Features', to: '/features' },
                { label: 'Model hub', to: '/model-hub' },
                { label: 'Knowledge', to: '/knowledge' },
                { label: 'Workspace', to: '/workspace' },
                { label: 'Roadmap', to: '/roadmap' },
                { label: 'FAQ', to: '/faq' },
                { label: 'Download', to: '/download' },
                { label: 'Blog', to: '/blog' },
              ]}
            />
            <FooterColumn
              title="Get started"
              links={[
                { label: 'Download the app', to: '/download' },
                { label: 'Use it in your browser', to: '/login' },
                { label: 'Create an account', to: '/signup' },
                { label: 'Sign in', to: '/login' },
              ]}
            />
            <FooterColumn
              title="Company"
              links={[
                { label: 'About Us', href: 'https://nevtan.com/about-us' },
                { label: 'Contact Us', href: 'https://nevtan.com/contact-us' },
                { label: 'Trust & Security', href: 'https://nevtan.com/security' },
                { label: 'Resources', href: 'https://nevtan.com/resources' },
              ]}
            />
            <FooterColumn
              title="NevTan suite"
              links={[
                { label: 'NevTan Mail', href: 'https://mail.nevtan.com/' },
                { label: 'NevTan Drive', href: 'https://drive.nevtan.com/' },
                { label: 'NevTan Cloud', href: 'https://cloud.nevtan.com/' },
                { label: 'NevTan Sign', href: 'https://sign.nevtan.com/' },
                { label: 'NevTan Engage', href: 'https://engage.nevtan.com/' },
                { label: 'Explore the suite', href: 'https://nevtan.com' },
              ]}
            />
          </div>
        </div>

        {/* The site is allowed to be ambitious only because this stays on it. */}
        <p className="mt-12 border-t border-white/[0.12] pt-6 text-[12.5px] leading-relaxed text-white/55">
          This site describes the NevTan AI platform, including features still in development.
          Anything not marked &ldquo;Available today&rdquo; is not available yet. Model names are
          given as examples of what each category can include. Every model has its own license
          and terms.
        </p>
      </div>

      <div className="border-t border-white/[0.12]">
        <div className="nv-container flex flex-wrap items-center justify-between gap-x-3 gap-y-2 py-5">
          <p className="text-[13px] text-white/60">
            &copy; {new Date().getFullYear()} NevTan, Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-4 text-[13px]">
            {[
              { label: 'Privacy Policy', href: 'https://nevtan.com/privacy' },
              { label: 'Terms of Service', href: 'https://nevtan.com/term-condition' },
              { label: 'Contact', href: 'https://nevtan.com/contact-us' },
            ].map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noreferrer"
                className="text-white/60 transition hover:text-white"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  title,
  links,
}: {
  title: string
  /** `to` for a page on this site, `href` for anywhere else. */
  links: { label: string; to?: string; href?: string }[]
}) {
  const linkClass = 'text-[14px] text-white/[0.72] transition hover:text-white'
  return (
    <div>
      <h3 className="mb-4 text-[12px] font-extrabold uppercase tracking-[0.05em] text-brand-bright">
        {title}
      </h3>
      <ul className="space-y-2.5">
        {links.map((link) => (
          <li key={link.label}>
            {link.to ? (
              <Link to={link.to} className={linkClass}>
                {link.label}
              </Link>
            ) : (
              <a href={link.href} target="_blank" rel="noreferrer" className={linkClass}>
                {link.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}
